// ---------------------------------------------------------------------------
// Shared: Formula Evaluator
//
// Evaluates arithmetic formulas in the context of a data object.
// Used by tile_row tiles that declare `value.formula` expressions
// (e.g. "net_profit / revenue * 100").
//
// Safety: only allows numeric literals and basic arithmetic operators.
// No function calls, no property access beyond provided variable names.
// ---------------------------------------------------------------------------

/**
 * Evaluate an arithmetic formula against a data context.
 *
 * Variable names in the formula are replaced with values from dataContext.
 * Only variable names matching /^[a-zA-Z_]\w*$/ are substituted.
 * The final expression must consist only of digits, whitespace, and + - * / ( ) .
 *
 * Returns null if:
 *   - Any variable is null/undefined in the context
 *   - The expression contains disallowed characters
 *   - The result is not a finite number
 *
 * @param {string} formula — e.g. "net_profit / revenue * 100"
 * @param {object} dataContext — { net_profit: 380, revenue: 4200, ... }
 * @returns {number|null}
 */
export function evaluateFormula(formula, dataContext) {
  if (!formula || !dataContext) return null;
  try {
    // Only the variables the formula actually references matter. Iterating
    // ALL context keys (the old behavior) meant one unrelated null key
    // (e.g. gross_margin: null on the same tile block) killed every formula.
    const varsInFormula = [...new Set(String(formula).match(/[a-zA-Z_]\w*/g) || [])];
    // Sort by length descending — replace longer names first to avoid
    // partial matches (e.g. "net_profit" before "net")
    const sortedVars = varsInFormula.sort((a, b) => b.length - a.length);

    let expr = formula;
    for (const key of sortedVars) {
      const val = Number(dataContext[key]);
      if (dataContext[key] == null || !Number.isFinite(val)) return null;
      const regex = new RegExp(`\\b${key}\\b`, "g");
      expr = expr.replace(regex, String(val));
    }

    // Safety: only allow arithmetic expressions (e/E for exponent notation
    // that String(number) can produce, e.g. 1e-7)
    if (!/^[\d\s+\-*/().eE]+$/.test(expr)) return null;
    const result = Function(`"use strict"; return (${expr})`)();
    return Number.isFinite(result) ? result : null;
  } catch (e) {
    return null;
  }
}
